import React, { useMemo, useState } from 'react';
import { Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Typography, Stack, Button, Checkbox, Chip, } from '@mui/material';
import InvoiceCategoryTabs from '../../../../../components/Billing/Invoice/InvoiceCategoryTabs';
import TableSkeleton from '../../../../../components/common/TableSkeleton';
import useCurrency from '../../../../../components/CurrencyProvider';
import style from '../../../Advance/Advance.module.css';
import { CHECKED_ICON, UNCHECKED_ICON } from '../../../Invoice/common/InvoiceTableCommon';
import InvoiceLogs from './InvoiceLogs';
import { useAppQuery } from '../../../../../services/appQuery';
import { getPackageConfigConfigURL, getRefundListsURL, InvoiceServices, InvoiceRefundPaymentPayload, PaymentDetailPayload } from '../../Refund.helper';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, postRequest } from '../../../../../services/httpService';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem } from '../../../collectPayment';
import { usePatientContext } from '../../../../../components/patinetProvider';
import { useAlert } from '../../../../../components/alertProvider';
import { AdvanceConfigLookups } from '../../../Advance/advance.interfaces';
import { useMutation } from '@tanstack/react-query';
import { postMakeAdvancePaymentURL } from '../../../Advance/advance.urls';
import { useClinic } from '../../../../../components/ClinicProvider';
import { formatDateDMY } from '../../../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '../../../../../components/confirmationModal';

type InvoiceRefundTabKey = 'Refund_Invoices' | 'Refund_Logs';

const InvoiceDetails: React.FC = () => {
  const { formatCurrency,currency: providerCurrency } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [activeTab, setActiveTab] = useState<InvoiceRefundTabKey>('Refund_Invoices');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { refund_id } = useParams<{ id?: string; refund_id?: string }>();
  const refundId = Number(refund_id ?? 0);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [packageRefundConfirmOpen, setPackageRefundConfirmOpen] = useState(false);
  const [consumeFromPackageForModal, setConsumeFromPackageForModal] = useState<number>(0);
  const { patientId , MainpatientDetails } = usePatientContext();
  const { addAlert } = useAlert();
  const navigate = useNavigate();
  const { ClinicId } = useClinic();
  const toSafeNumber = (value: unknown) => Number(value) || 0;
  const patientDisplayName = `${MainpatientDetails?.first_name ?? ''} ${MainpatientDetails?.last_name ?? ''}`.trim() || tBilling('RefundDetails.common.patientFallback', 'Patient');
  const patientRefundRoute = patientId ? `/admin/billing/refund/patient-refund/${patientId}` : '/admin/billing/refund/refund-list';

  
  const { data: requestedRefunds, isLoading } = useAppQuery<InvoiceServices>(
    ['RequestedRefundDetails', refundId],
    () => getRequest<InvoiceServices>(`${getRefundListsURL()}${refundId}/`),
    {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
    }
  );

  const { data: PackageConfig, queryClient } = useAppQuery<AdvanceConfigLookups>(
    ['PackageConfigMaster'],
    () => getRequest<AdvanceConfigLookups>(`${getPackageConfigConfigURL()}`),
    { 
      enabled: true,
    }
  );

  const categories = useMemo(
    () => [
      { label: tBilling('RefundDetails.tabs.refundInvoices', 'Refund Invoices'), name:'Refund_Invoices'},
      { label: tBilling('RefundDetails.tabs.refundLogs', 'Refund Logs'), name:'Refund_Logs'},
    ],
    [tBilling]
  );

  const columns = useMemo(
    () => [
      '',
      tBilling('RefundDetails.invoiceColumns.invoiceNo', 'Invoice No.'),
      tBilling('Refund.date', 'Date'),
      tBilling('RefundDetails.invoiceColumns.code', 'Code'),
      tBilling('RefundDetails.invoiceColumns.service', 'Service'),
      tBilling('RefundDetails.invoiceColumns.package', 'Package'),
      tBilling('RefundDetails.invoiceColumns.provider', 'Provider'),
      tBilling('RefundDetails.invoiceColumns.billType', 'Bill Type'),
      tBilling('RefundDetails.invoiceColumns.charges', 'Charges'),
      tBilling('Refund.concession_amt', 'Concession Amt.'),
      tBilling('Refund.net_amt', 'Net Amt.'),
    ],
    [tBilling]
  );

  const invoiceItems = useMemo(() => requestedRefunds?.invoice_items || [], [requestedRefunds]);

  // Compute selectable (non-refunded) items once for reuse
  const selectableItems = useMemo(
    () => invoiceItems.filter(row => !row.refund_status),
    [invoiceItems]
  );

  const allSelected = useMemo(
    () => selectableItems.length > 0 && selectableItems.every((row) => selectedIds.includes(row.invoice_item_id)),
    [selectableItems, selectedIds]
  );

  const someSelected = useMemo(
    () => selectableItems.some((row) => selectedIds.includes(row.invoice_item_id)),
    [selectableItems, selectedIds]
  );

  const handleRowSelect = (id: number) => {
    const item = invoiceItems.find(row => row.invoice_item_id === id);
    if (item?.refund_status) return; // Don't allow selecting refunded items
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(invoiceItems.filter(row => !row.refund_status).map((row) => row.invoice_item_id));
    } else {
      setSelectedIds([]);
    }
  };

  const totalInvoicedAmount = useMemo(
    () => invoiceItems.reduce((sum, row) => sum + toSafeNumber(row.net_amount), 0),
    [invoiceItems]
  );

  const refundAmount = useMemo(
    () =>
      invoiceItems
        .filter((row) => selectedIds.includes(row.invoice_item_id))
        .reduce((sum, row) => sum + toSafeNumber(row.net_amount), 0),
    [selectedIds, invoiceItems]
  );

  const remainingAmount = totalInvoicedAmount - refundAmount;

  const refundSummary = useMemo(
    () => [
      { label: tBilling('RefundDetails.summary.totalInvoicedAmount', 'Total Invoiced Amount'), value: totalInvoicedAmount.toFixed(2) },
      { label: tBilling('RefundDetails.summary.refundAmount', 'Refund Amount'), value: refundAmount.toFixed(2) },
      { label: tBilling('RefundDetails.summary.remainingAmount', 'Remaining Amount'), value: remainingAmount.toFixed(2) },
    ],
    [totalInvoicedAmount, refundAmount, remainingAmount, tBilling]
  );

  const { mutate: submitPayment, isPending: isSubmittingRefund } = useMutation({
    mutationFn: (payload: InvoiceRefundPaymentPayload) => postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      addAlert(tBilling('RefundDetails.messages.refundSentSuccess', 'Refund sent successfully.'), 'success');
      setCollectPaymentOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['RequestedRefundList'] });
      navigate(patientRefundRoute);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('RefundDetails.messages.refundSentFailure', 'Failed to send refund.');
      addAlert(message, 'error');
    },
  });

  const handleOpenCollectPayment = (netAmount: number, patientName: string, patientIdValue: string) => {
    setPaymentDetailsForModal({
      date: new Date().toLocaleDateString('en-GB'),
      patientName: patientName || 'Patient',
      patientId: patientIdValue || '',
      patientNumericId: patientId ?? undefined,
      mrnNo: MainpatientDetails?.medical_record_no ?? '',
      currency: providerCurrency,
      netAmount: netAmount,
    });
    setCollectPaymentOpen(true);
  };


  // Separate selected items into package and non-package groups
  const selectedItems = useMemo(
    () => invoiceItems.filter((row) => selectedIds.includes(row.invoice_item_id)),
    [selectedIds, invoiceItems]
  );

  const packageItems = useMemo(
    () => selectedItems.filter((row) => row.package_id),
    [selectedItems]
  );

  const nonPackageItems = useMemo(
    () => selectedItems.filter((row) => !row.package_id),
    [selectedItems]
  );

  const packageRefundAmount = useMemo(
    () => packageItems.reduce((sum, row) => sum + toSafeNumber(row.net_amount), 0),
    [packageItems]
  );

  const nonPackageRefundAmount = useMemo(
    () => nonPackageItems.reduce((sum, row) => sum + toSafeNumber(row.net_amount), 0),
    [nonPackageItems]
  );

  const allSelectedArePackage = selectedItems.length > 0 && nonPackageItems.length === 0;

  /**
   * Submit refund payment with package advance entries auto-added.
   * Package items get refunded back to package advance (method: 'Package Advance').
   * Non-package items get refunded via the user-selected payment method from the modal.
   */
  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[] = []) => {
    if (!refundId) {
      addAlert(tBilling('RefundDetails.messages.invalidRefundReference', 'Invalid refund reference.'), 'error');
      return;
    }

    const currency = paymentDetailsForModal?.currency ?? providerCurrency ?? 'INR';
    const isoOrNull = (d?: string) => (d ? new Date(d).toISOString() : new Date().toISOString());

    // Build package advance payment entries for package items
    const packagePaymentDetails: PaymentDetailPayload[] = [];
    if (packageItems.length > 0) {
      const advanceMode = PackageConfig?.modeofpayment?.find(
        (mode) => mode.name?.toLowerCase() === 'advance' || String(mode.code ?? '').toLowerCase() === 'advance'
      );

      if (advanceMode) {
        // Group by package_id and sum amounts
        const packageAmountMap = new Map<number, number>();
        packageItems.forEach((item) => {
          const pkgId = item.package_id!;
          const current = packageAmountMap.get(pkgId) || 0;
          packageAmountMap.set(pkgId, current + toSafeNumber(item.net_amount));
        });

        packageAmountMap.forEach((amount, packageId) => {
          packagePaymentDetails.push({
            payment_mode_id: advanceMode.id,
            paid_amount: String(amount),
            currency,
            number: null,
            date: new Date().toISOString(),
            bank_id: null,
            account_name: null,
            branch_name: null,
            ifsc_code: null,
            remarks: `Refunded to package advance (Package ID: ${packageId})`,
            cheque_number: null,
            cheque_date: null,
          });
        });
      }
    }

    // Build regular payment entries from modal history (for non-package items)
    const regularPaymentDetails: PaymentDetailPayload[] = history.map((item) => ({
      payment_mode_id: item.payment_mode_id,
      paid_amount: String(item.amount ?? 0),
      currency,
      number: item.transactionNumber ?? null,
      date: isoOrNull(item.date ?? undefined),
      bank_id: null,
      account_name: item.bankName ?? null,
      branch_name: null,
      ifsc_code: null,
      remarks: item.notes ?? null,
      cheque_number: item.chequeNumber ?? null,
      cheque_date: item.chequeDate ? new Date(item.chequeDate).toISOString() : null,
    }));

    // Combine: package advance entries + regular payment entries
    const allPaymentDetails = [...packagePaymentDetails, ...regularPaymentDetails];

    if (!allPaymentDetails.length) {
      addAlert(tBilling('RefundDetails.messages.missingPaymentHistory', 'Missing payment history. Please try again.'), 'error');
      return;
    }

    const payload: InvoiceRefundPaymentPayload = {
      payment_against_type: 'REFUND',
      refund_id: refundId,
      refund_type: 'INVOICE',
      invoice_items: selectedIds,
      refund_amount: refundAmount.toFixed(2),
      clinic_id: toSafeNumber(ClinicId),
      remarks: history[0]?.notes ?? '',
      payment_details: allPaymentDetails,
    };

    submitPayment(payload);
  };

  /**
   * Handle "Refund Payment" button click.
   * If all selected items are from a package, submit directly without opening payment modal.
   * Otherwise, open the payment modal for non-package amount.
   */
  const handleRefundPaymentClick = () => {
    if (allSelectedArePackage) {
      // All items are from package — show confirmation before refunding to package advance
      setPackageRefundConfirmOpen(true);
    } else if (packageItems.length > 0) {
      // Mixed: some package, some non-package — open modal showing total with package consume amount
      setConsumeFromPackageForModal(packageRefundAmount);
      handleOpenCollectPayment(refundAmount, patientDisplayName, String(patientId ?? ''));
    } else {
      // No package items — open modal for full refund amount (current behavior)
      setConsumeFromPackageForModal(0);
      handleOpenCollectPayment(refundAmount ?? 0, patientDisplayName, String(patientId ?? ''));
    }
  };
  return (
    <Box
      sx={{ px: 3, py: 2 }}
      data-testid="invoice-details-container-main"
      aria-label={tBilling('RefundDetails.aria.invoiceDetailsContainer', 'Invoice refund details container')}
    >
      <Stack direction="row" justifyContent="flex-start" sx={{ mb: 2 }} data-testid="invoice-refund-tabs-section">
        <InvoiceCategoryTabs
          activeCategory={activeTab}
          categories={categories}
          onCategoryChange={(cat) => setActiveTab(cat.name as InvoiceRefundTabKey)}
        />
      </Stack>

      {activeTab === 'Refund_Invoices' && (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="invoice-refund-table-container">
            <Table aria-label={tBilling('RefundDetails.aria.refundInvoiceTable', 'Refund invoices list')} data-testid="invoice-details-table-list">
              <TableHead>
                <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                  {columns.map((col, idx) => (
                    <TableCell
                      key={idx === 0 ? 'select' : col}
                      className={style.tableHeader}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {idx === 0 ? (
                        <Checkbox
                          icon={UNCHECKED_ICON}
                          checkedIcon={CHECKED_ICON}
                          indeterminate={someSelected && !allSelected}
                          checked={allSelected}
                          onChange={handleSelectAll}
                          data-testid="invoice-details-checkbox-select-all"
                          slotProps={{
                            input: {
                              'aria-label': tBilling('RefundDetails.aria.selectAllInvoices', 'Select all invoice rows')
                            }
                          }}
                        />
                      ) : (
                        col
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableSkeleton
                    columnWidths={[40, 120, 120, 120, 80, 260, 140, 160, 120, 120, 200, 120]}
                    rows={10}
                  />
                )}

                {!isLoading &&
                  invoiceItems.map((row) => (
                    <TableRow
                      hover
                      key={row.invoice_item_id}
                      tabIndex={0}
                      aria-label={tBilling('RefundDetails.aria.invoiceRow', 'Invoice row')}
                      data-testid={`invoice-details-row-${row.invoice_item_id}`}
                      sx={{ opacity: row.refund_status ? 0.7 : 1 }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRowSelect(row.invoice_item_id);
                        }
                      }}
                    >
                      <TableCell className={style.tableCells}>
                        <Checkbox
                          checked={selectedIds.includes(row.invoice_item_id)}
                          onChange={() => handleRowSelect(row.invoice_item_id)}
                          icon={UNCHECKED_ICON}
                          checkedIcon={CHECKED_ICON}
                          disableRipple
                          disabled={!!row.refund_status}
                          data-testid={`invoice-details-checkbox-row-${row.invoice_item_id}`}
                          slotProps={{
                            input: {
                              'aria-label': tBilling('RefundDetails.aria.selectInvoiceRow', 'Select invoice row')
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className={style.tableCells} sx={{ color: '#007AFF', fontWeight: 600 }}>
                        {row.invoice_number ?? '-'}
                      </TableCell>
                      <TableCell className={style.tableCells}>{row.date ? formatDateDMY(row.date) : '-'}</TableCell>
                      <TableCell className={style.tableCells}>{row.invoice_item_code ?? '-'}</TableCell>
                      <TableCell className={style.tableCells}>
                        <Box>
                          <Typography variant="body2">{row.service_name ?? '-'}</Typography>
                          {row.refund_status && (
                            <Chip
                              label={tBilling('RefundDetails.status.refunded', 'Refunded')}
                              size="small"
                              sx={{
                                backgroundColor: '#D1FAE5',
                                color: '#065F46',
                                fontWeight: 500,
                                fontSize: 11,
                                height: 20,
                                mt: 0.5,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className={style.tableCells}>{row.package_name ?? '-'}</TableCell>
                      <TableCell className={style.tableCells}>{row.provider_name ?? '-'}</TableCell>
                      <TableCell className={style.tableCells}>{row.bill_type ?? '-'}</TableCell>
                      <TableCell className={style.tableCells}>
                        {formatCurrency(toSafeNumber(row.charges))}
                      </TableCell>
                      <TableCell className={style.tableCells}>
                        {`${formatCurrency(toSafeNumber(row.concession_amount))}`}
                      </TableCell>
                      <TableCell className={style.tableCells}>
                        {formatCurrency(toSafeNumber(row.net_amount))}
                      </TableCell>
                    </TableRow>
                  ))}

                {!isLoading && invoiceItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">{tBilling('Refund.no_entries_found', 'No entries found.')}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 4, px: 1 }} data-testid="invoice-details-container-summary">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {tBilling('RefundDetails.summary.title', 'Refund Details')}
            </Typography>
            <Stack direction="row" spacing={6} sx={{ mb: 2 }} data-testid="invoice-details-summary-values">
              {refundSummary.map((item) => (
                <Stack key={item.label} direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body1">:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {item.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {/* Package refund breakdown - shown when selected items include package items */}
            {packageItems.length > 0 && selectedIds.length > 0 && (
              <Stack direction="row" spacing={6} sx={{ mb: 4 }} data-testid="invoice-details-package-refund-breakdown">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#805ad5' }}>
                    {tBilling('RefundDetails.summary.refundToPackage', 'Refund to Package')}
                  </Typography>
                  <Typography variant="body1">:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#805ad5' }}>
                    {packageRefundAmount.toFixed(2)}
                  </Typography>
                </Stack>
                {nonPackageItems.length > 0 && (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#38a169' }}>
                      {tBilling('RefundDetails.summary.cashRefund', 'Cash Refund')}
                    </Typography>
                    <Typography variant="body1">:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#38a169' }}>
                      {nonPackageRefundAmount.toFixed(2)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                sx={{ borderRadius: 3, minWidth: 120 }}
                disabled={isSubmittingRefund}
                onClick={() => navigate(patientRefundRoute)}
                data-testid="invoice-details-button-cancel"
                aria-label={tBilling('RefundDetails.aria.cancelRefundPayment', 'Cancel refund payment')}
              >
                {tBilling('package_details.cancel', 'Cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ borderRadius: 3, minWidth: 120 }}
                disabled={selectedIds.length === 0 || isSubmittingRefund}
                onClick={handleRefundPaymentClick}
                data-testid="invoice-details-button-refund-payment"
                aria-label={tBilling('RefundDetails.aria.openRefundPaymentModal', 'Open refund payment modal')}
              >
                {tBilling('RefundDetails.actions.refundPayment', 'Refund Payment')}
              </Button>
            </Stack>

            {paymentDetailsForModal && (
              <CollectPaymentModal
                open={collectPaymentOpen}
                onClose={() => {
                  setCollectPaymentOpen(false);
                  setConsumeFromPackageForModal(0);
                }}
                onSave={handlePaymentSave}
                paymentDetails={paymentDetailsForModal}
                existingPaymentHistory={[]}
                walletAdvances={[]}
                paymentModes={PackageConfig?.modeofpayment || []}
                hidePaymentModeToggle={true}
                showWallet={true}
                title={tBilling('collectPayment.refundPaymentTitle', 'Refund Payment')}
                isSubmitting={isSubmittingRefund}
                consumeFromPackage={consumeFromPackageForModal}
                consumePackageBannerLabel={tBilling('RefundDetails.packageBanner.bannerLabel', 'Refund to package advance :')}
                consumePackageTotalLabel={tBilling('RefundDetails.packageBanner.totalRefund', 'Total Refund')}
                consumePackageDeductedLabel={tBilling('RefundDetails.packageBanner.refundToPackage', 'Refund to Package')}
                consumePackagePendingLabel={tBilling('RefundDetails.packageBanner.cashRefund', 'Cash Refund')}
                requireFullPayment={true}
              />
            )}

            {/* Package Refund Confirmation Modal */}
            <ConfirmationModal
              open={packageRefundConfirmOpen}
              handleClose={() => setPackageRefundConfirmOpen(false)}
              onConfirm={() => {
                setPackageRefundConfirmOpen(false);
                handlePaymentSave({} as PaymentFormValues, []);
              }}
              type="save"
              title={tBilling('RefundDetails.packageRefundConfirm.title', 'Refund to Package')}
              contentMessage={tBilling(
                'RefundDetails.packageRefundConfirm.message',
                `Are you sure you want to refund ${formatCurrency(packageRefundAmount)} back to the package advance? This action cannot be undone.`
              )}
              isConfirming={isSubmittingRefund}
            />
          </Box>
        </>
      )}
      {activeTab === 'Refund_Logs' && (
        <InvoiceLogs />
      )}
    </Box>
  );
};

export default InvoiceDetails;

